import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import AuthCallback from "@/components/AuthCallback";
import { UpdateBanner } from "@/components/UpdateBanner";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Customers from "@/pages/Customers";
import Quotes from "@/pages/Quotes";
import QuoteForm from "@/pages/QuoteForm";
import Inventories from "@/pages/Inventories";
import InventoryForm from "@/pages/InventoryForm";
import Proformas from "@/pages/Proformas";
import ProformaForm from "@/pages/ProformaForm";
import Settings from "@/pages/Settings";
import AuditLog from "@/pages/AuditLog";
import Archive from "@/pages/Archive";
import Profile from "@/pages/Profile";
import Calendar from "@/pages/Calendar";
import Operators from "@/pages/Operators";
import SuperAdmin from "@/pages/SuperAdmin";
import GoogleCallback from "@/pages/GoogleCallback";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="w-6 h-6 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function OwnerRoute({ children }) {
  const { user } = useAuth();
  if (user?.role !== "OWNER") return <Navigate to="/dashboard" replace />;
  return children;
}

function SuperAdminRoute({ children }) {
  const { user } = useAuth();
  if (!user?.is_super_admin) return <Navigate to="/dashboard" replace />;
  return children;
}

function PermissionRoute({ permission, children }) {
  const { hasPermission } = useAuth();
  if (!hasPermission(permission)) return <Navigate to="/dashboard" replace />;
  return children;
}

function DefaultRedirect() {
  const { user } = useAuth();
  if (user?.is_super_admin) return <Navigate to="/super-admin" replace />;
  return <Navigate to="/dashboard" replace />;
}

function AppRouter() {
  const location = useLocation();
  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/google-callback" element={<GoogleCallback />} />
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<DefaultRedirect />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="calendar" element={<Calendar />} />
        <Route path="customers" element={<Customers />} />
        <Route path="quotes" element={<Quotes />} />
        <Route path="quotes/new" element={<PermissionRoute permission="quotes.create"><QuoteForm /></PermissionRoute>} />
        <Route path="quotes/:id" element={<QuoteForm />} />
        <Route path="inventories" element={<Inventories />} />
        <Route path="inventories/new" element={<PermissionRoute permission="inventories.create"><InventoryForm /></PermissionRoute>} />
        <Route path="inventories/:id" element={<InventoryForm />} />
        <Route path="proformas" element={<PermissionRoute permission="proformas.create"><Proformas /></PermissionRoute>} />
        <Route path="proformas/new" element={<PermissionRoute permission="proformas.create"><ProformaForm /></PermissionRoute>} />
        <Route path="proformas/:id" element={<PermissionRoute permission="proformas.create"><ProformaForm /></PermissionRoute>} />
        <Route path="settings" element={<PermissionRoute permission="settings.manage"><Settings /></PermissionRoute>} />
        <Route path="archive" element={<PermissionRoute permission="archive.view"><Archive /></PermissionRoute>} />
        <Route path="operators" element={<PermissionRoute permission="users.manage"><Operators /></PermissionRoute>} />
        <Route path="audit-log" element={<PermissionRoute permission="audit.view"><AuditLog /></PermissionRoute>} />
        <Route path="profile" element={<Profile />} />
        <Route path="super-admin" element={<SuperAdminRoute><SuperAdmin /></SuperAdminRoute>} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <UpdateBanner />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
